import {
  type Column,
  type ColumnRenderer,
  DateRenderer,
  type ExplorerDataProvider,
  ImgElement,
  SelectionExplorer,
  createInMemoryDataProvider,
  findAnywhereInStringCaseInsensitive,
  findExact,
  sortToPostGraphileOrderBy,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import {
  ContainerImagesDocument,
  type ContainerImagesQuery,
  type ContainerImagesQueryVariables,
  PiletArtifactsDocument,
  type PiletArtifactsQuery,
  type PiletArtifactsQueryVariables,
  ServiceDeploymentManifestsDocument,
  ServiceDeploymentManifestsOrderBy,
  type ServiceDeploymentManifestsQuery,
  type ServiceDeploymentManifestsQueryVariables,
} from 'generated/hosting-service';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { type ReactNode, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { PiletArtifact } from '../../CustomizableServiceDetails/PiletArtifacts/PiletArtifactsExplorer.types';
import { getDeploymentStateIconLabel } from '../../common';
import classes from '../CustomizableServiceDeployment.module.scss';
import { useRowDataFilters } from './RowExplorer.filters';
import type {
  ContainerImage,
  DeploymentData,
  RowExplorerProps,
  ServiceDeploymentManifestWithCommonTypes,
} from './RowExplorer.types';

interface ResourceWithDeploymentStatus {
  usedInActiveDeployment: boolean;
}

export const RowExplorer: React.FC<RowExplorerProps> = (props) => {
  const [filteredContainerImageData, setFilteredContainerImageData] = useState<
    ContainerImage[]
  >([]);
  const [filteredPiletArtifactsData, setFilteredPiletArtifactsData] = useState<
    PiletArtifact[]
  >([]);
  const [filteredDeploymentManifestData, setFilteredDeploymentManifestData] =
    useState<ServiceDeploymentManifestWithCommonTypes[]>([]);
  const { filterOptions, transformFilters } = useRowDataFilters(
    props.type,
    props.excludeItems,
  );

  const deploymentStatusRenderer: ColumnRenderer<
    ResourceWithDeploymentStatus
  > = (_value, data): ReactNode => {
    const { label, icon } = getDeploymentStateIconLabel(
      data.usedInActiveDeployment,
    );
    return (
      <div className={classes.status_container}>
        <ImgElement
          src={icon}
          style={{ width: '20px', height: '20px' }}
          decorative={true}
        />
        <div className={classes.status}>{label}</div>
      </div>
    );
  };

  const getColumnsByType = (type: string): Column<DeploymentData['id']>[] => {
    switch (type) {
      case 'ContainerImage':
        return [
          {
            label: 'Container Image Version',
            propertyName: 'containerImageVersion',
          },
          {
            label: 'Deployment State',
            propertyName: 'usedInActiveDeployment',
            render: deploymentStatusRenderer,
          },
        ];
      case 'ServicePiletArtifacts':
        return [
          {
            label: 'Package Name',
            propertyName: 'name',
            size: '2fr',
          },
          {
            label: 'Package Version',
            propertyName: 'packageVersion',
            size: '2fr',
          },
          {
            label: 'Deployment State',
            propertyName: 'usedInActiveDeployment',
            render: deploymentStatusRenderer,
          },
        ];
      case 'ServiceDeploymentManifest':
        return [
          {
            label: 'Name',
            propertyName: 'name',
            size: '2fr',
          },
          {
            label: 'Deployment State',
            propertyName: 'usedInActiveDeployment',
            render: deploymentStatusRenderer,
          },
          {
            label: 'Created At',
            propertyName: 'createdDate',
            render: DateRenderer,
          },
        ];
      default:
        return [];
    }
  };

  const { envId, customizableServiceId } = useParams<{
    envId: string;
    customizableServiceId: string;
  }>();
  // Data provider
  const dataProvider: ExplorerDataProvider<DeploymentData> = {
    // Add DeploymentData as a type after removing hard code result
    loadData: async ({ pagingInformation, sorting, filters }) => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
      );
      if (props.type === 'ContainerImage') {
        if (filteredContainerImageData.length === 0) {
          const result = await envApolloClient?.query<
            ContainerImagesQuery,
            ContainerImagesQueryVariables
          >({
            query: ContainerImagesDocument,
            variables: {
              id: customizableServiceId,
            },
            fetchPolicy: 'network-only',
          });
          const containerImageData: ContainerImage[] = [];

          result?.data?.serviceDefinition?.repositoryTags.map((item, index) => {
            const imageData = {
              id: index,
              containerImageVersion: item.containerImageVersion,
              usedInActiveDeployment:
                result?.data?.serviceDefinition?.serviceDeployments.nodes[0] !==
                  undefined &&
                item.tag ===
                  result?.data?.serviceDefinition?.serviceDeployments.nodes[0]
                    .containerImageVersion,
            };
            containerImageData.push(imageData);
            setFilteredContainerImageData(containerImageData ?? []);
          });

          return {
            totalCount: containerImageData.length,
            filteredCount: containerImageData.length,
            data: containerImageData,
            pagingInformation: 1,
            hasMoreData: false,
          };
        } else {
          const actualProvider = createInMemoryDataProvider<DeploymentData>(
            filteredContainerImageData,
            {
              filterFunctions: {
                containerImageVersion: findAnywhereInStringCaseInsensitive,
              },
            },
          );
          // Increase page count
          const currentPage = (pagingInformation as number) ?? 0;

          // Use the in memory provider to do the heavy lifting
          const { totalCount, filteredCount, data } =
            await actualProvider.loadData({
              pagingInformation,
              filters,
              sorting,
            });

          // Apply paging to the results
          const pageSize = 20;
          return {
            totalCount: totalCount,
            filteredCount: filteredCount,
            data: data?.slice(
              pageSize * currentPage,
              pageSize * (currentPage + 1),
            ),
            pagingInformation: currentPage + 1,
            hasMoreData: currentPage < 9,
          };
        }
      } else if (props.type === 'ServicePiletArtifacts') {
        if (filteredPiletArtifactsData.length === 0) {
          const result = await envApolloClient?.query<
            PiletArtifactsQuery,
            PiletArtifactsQueryVariables
          >({
            query: PiletArtifactsDocument,
            variables: {
              id: customizableServiceId,
            },
            fetchPolicy: 'network-only',
          });
          const piletArtifactData: React.SetStateAction<
            PiletArtifact[] | undefined
          > = [];
          result?.data?.serviceDefinition?.servicePiletArtifacts.nodes
            .filter((pilet) => !props.excludeItems?.includes(pilet.name))
            .map((item) => {
              const piletData = {
                id: item.id,
                name: item.name,
                packageVersion: item.packageVersion,
                usedInActiveDeployment:
                  result?.data?.serviceDefinition?.serviceDeployments?.nodes[0]?.servicePiletArtifactIds?.includes(
                    item.id,
                  ) ?? false,
              };
              piletArtifactData.push(piletData);
              setFilteredPiletArtifactsData(piletArtifactData);
            });
          return {
            totalCount: piletArtifactData.length,
            filteredCount: piletArtifactData.length,
            data: piletArtifactData,
            pagingInformation: 1,
            hasMoreData: false,
          };
        } else {
          const actualProvider = createInMemoryDataProvider<DeploymentData>(
            filteredPiletArtifactsData ?? [],
            {
              filterFunctions: {
                name: findAnywhereInStringCaseInsensitive,
                packageVersion: findAnywhereInStringCaseInsensitive,
                usedInActiveDeployment: findExact,
              },
            },
          );
          // Increase page count
          const currentPage = (pagingInformation as number) ?? 0;
          // Use the in memory provider to do the heavy lifting
          const { totalCount, filteredCount, data } =
            await actualProvider.loadData({
              pagingInformation,
              filters,
              sorting,
            });

          // Apply paging to the results
          const pageSize = 20;
          return {
            totalCount: totalCount,
            filteredCount: filteredCount,
            data: data?.slice(
              pageSize * currentPage,
              pageSize * (currentPage + 1),
            ),
            pagingInformation: currentPage + 1,
            hasMoreData: currentPage < 9,
          };
        }
      } else if (props.type === 'ServiceDeploymentManifest') {
        if (filteredDeploymentManifestData.length === 0) {
          const result = await envApolloClient?.query<
            ServiceDeploymentManifestsQuery,
            ServiceDeploymentManifestsQueryVariables
          >({
            query: ServiceDeploymentManifestsDocument,
            variables: {
              condition: {
                serviceDefinitionId: customizableServiceId,
              },
              filter: transformFilters(filters),
              orderBy: sortToPostGraphileOrderBy(
                sorting,
                ServiceDeploymentManifestsOrderBy,
              ),
              after: pagingInformation,
            },
            fetchPolicy: 'network-only',
          });

          const serviceDeploymentManifestData =
            result?.data?.filtered?.nodes
              .filter(
                (manifest) => !props.excludeItems?.includes(manifest.name),
              )
              .map((item) => ({
                id: item.id,
                name: item.name,
                createdDate: item.createdDate,
                usedInActiveDeployment: item.currentlyInUse ?? false,
              })) ?? [];

          const hasMoreData =
            result?.data?.filtered?.pageInfo?.hasNextPage ?? false;

          // Cache only single-page result sets. Multi-page datasets must stay
          // API-driven (cursor paging) and must not switch to in-memory paging.
          if (!hasMoreData) {
            setFilteredDeploymentManifestData(serviceDeploymentManifestData);
          }

          return {
            data: serviceDeploymentManifestData ?? [],
            totalCount: result?.data?.nonFiltered?.totalCount ?? 0,
            filteredCount: result?.data?.filtered?.totalCount,
            hasMoreData,
            pagingInformation: result?.data?.filtered?.pageInfo?.endCursor,
          };
        } else {
          const actualProvider = createInMemoryDataProvider<DeploymentData>(
            filteredDeploymentManifestData,
            {
              filterFunctions: {
                name: findAnywhereInStringCaseInsensitive,
                usedInActiveDeployment: findExact,
              },
            },
          );
          // Increase page count
          const currentPage = (pagingInformation as number) ?? 0;

          // Use the in memory provider to do the heavy lifting
          const { totalCount, filteredCount, data } =
            await actualProvider.loadData({
              pagingInformation,
              filters,
              sorting,
            });

          // Apply paging to the results
          const pageSize = 20;
          return {
            totalCount: totalCount,
            filteredCount: filteredCount,
            data: data?.slice(
              pageSize * currentPage,
              pageSize * (currentPage + 1),
            ),
            pagingInformation: currentPage + 1,
            hasMoreData: currentPage < 9,
          };
        }
      } else {
        return {
          data: [],
          totalCount: 0,
          filteredCount: 0,
          hasMoreData: false,
          pagingInformation: '',
        };
      }
    },
  };

  return (
    <SelectionExplorer<DeploymentData['id']>
      {...props}
      columns={getColumnsByType(props.type)}
      dataProvider={dataProvider}
      defaultSortOrder={{ column: 'updatedDate', direction: 'desc' }}
      filterOptions={filterOptions}
    />
  );
};
